import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser, userKuerzelFromUser } from "@/lib/auth";
import {
  composeAnfrageMail,
  createAnfrageDraft,
  deleteAnfrageDraft,
  finalizeAnfrage,
  finalizeAnfrageMulti,
  listAnfrageDrafts,
  patchAnfrageMailContent,
  sendAnfrageGroupMail,
  sendAnfrageMail,
  updateAnfrageDraft,
  type AnfrageDraftOut,
  type AnfrageMailReviewItem,
  type SupplierRecipientIn,
} from "@/lib/anfragen";
import { NewAnfrageDialog } from "@/pages/anfragen/NewAnfrageDialog";
import { TaMailReviewDialog, type TaMailReviewResult } from "@/pages/anfragen/TaMailReviewDialog";
import {
  buildAnfrageMailReviewItems,
  buildMailtoHref,
  clearLocalDraft,
  createRecipient,
  emptyWizardState,
  newPositionLocalId,
  persistLocalDraft,
  restoreLocalDraft,
  stateToDraftIn,
  validateTaRecipients,
  type AnfrageWizardState,
  type TaRecipient,
} from "@/pages/anfragen/anfrageWizardUtils";

function draftRecipientFromRow(row: SupplierRecipientIn & { supplier_id?: string | number | null }): TaRecipient {
  const hasHero = !!(row.supplier_id && String(row.supplier_id).trim());
  if (hasHero) {
    return createRecipient({
      mode: "hero",
      supplier: { id: String(row.supplier_id), name: row.supplier_name || "" },
      contact: {
        email: row.supplier_email || "",
        first_name: row.supplier_contact_first_name || "",
        last_name: row.supplier_contact_last_name || "",
        anrede: row.supplier_contact_anrede || "",
      },
    });
  }
  return createRecipient({
    mode: "manual",
    manual: {
      firm: row.supplier_name || "",
      anrede: row.supplier_contact_anrede || "",
      name: row.supplier_contact_name || "",
      email: row.supplier_email || "",
    },
  });
}

function draftToWizardState(d: AnfrageDraftOut, kuerzel: string): AnfrageWizardState {
  const primary = draftRecipientFromRow({
    supplier_id: d.supplier_id,
    supplier_name: d.supplier_name,
    supplier_email: d.supplier_email,
    supplier_contact_first_name: d.supplier_contact_first_name,
    supplier_contact_last_name: d.supplier_contact_last_name,
    supplier_contact_anrede: d.supplier_contact_anrede,
    supplier_contact_name: d.supplier_contact_name,
  });
  const zusatz = (d.zusatz_empfaenger || []).map((z) => draftRecipientFromRow(z));
  return {
    recipients: [primary, ...zusatz],
    kuerzel: d.kuerzel || kuerzel,
    auftraege: d.auftraege || [],
    positionen: (d.positionen || []).map((p) => ({ ...p, localId: newPositionLocalId() })),
    taPreview: {},
    draftId: d.id,
    snippetTemplateId: d.snippet_template_id ?? null,
    mailSubject: "",
    mailBody: "",
    placeholders: (d.auftraege || []).length ? 0 : 1,
    sourceMaId:
      d.source_ma_id ??
      (d as { source_ma?: { id?: number } }).source_ma?.id ??
      null,
  };
}

function firstRecipientEmail(state: AnfrageWizardState): string {
  const r = state.recipients[0];
  if (!r) return "";
  if (r.mode === "hero") return (r.contact?.email || "").trim();
  return (r.manual.email || "").trim();
}

export interface AnfrageWizardHostHandle {
  openNew: () => void;
  openDraft: (draft: AnfrageDraftOut, opts?: { sourceMaId?: number; title?: string }) => void;
  openDraftById: (
    id: number,
    opts?: { sourceMaId?: number; title?: string },
  ) => Promise<void>;
}

export const AnfrageWizardHost = forwardRef<
  AnfrageWizardHostHandle,
  {
    onOpenChange: (open: boolean) => void;
    emitNotice: (message: string) => void;
    emitChanged: () => void;
  }
>(function AnfrageWizardHost({ onOpenChange, emitNotice, emitChanged }, ref) {
  const { user } = useCurrentUser();
  const kuerzel = useMemo(() => userKuerzelFromUser(user), [user]);

  const isEntra = String(user?.auth_provider || "").toLowerCase() === "entra";

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardTitle, setWizardTitle] = useState("Neue Anfrage");
  const [wizardState, setWizardState] = useState<AnfrageWizardState>(() =>
    restoreLocalDraft(emptyWizardState()),
  );

  const [mailReview, setMailReview] = useState<{
    open: boolean;
    items: AnfrageMailReviewItem[];
    groupId: number | null;
    pendingCleanup: boolean;
    auftragIds: number[];
  }>({
    open: false,
    items: [],
    groupId: null,
    pendingCleanup: false,
    auftragIds: [],
  });

  const mailReviewRef = useRef(mailReview);
  mailReviewRef.current = mailReview;

  const wizardOpenRef = useRef(wizardOpen);
  wizardOpenRef.current = wizardOpen;

  const wizardStateRef = useRef(wizardState);
  wizardStateRef.current = wizardState;

  const composeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onOpenChange(wizardOpen);
  }, [wizardOpen, onOpenChange]);

  const debouncedCompose = useCallback((state: AnfrageWizardState) => {
    if (composeTimerRef.current) clearTimeout(composeTimerRef.current);
    composeTimerRef.current = setTimeout(() => {
      if (!wizardOpenRef.current) return;
      if (!state.auftraege.length) {
        setWizardState((s) => {
          if (!s.mailSubject && !s.mailBody && !Object.keys(s.taPreview).length) return s;
          return { ...s, mailSubject: "", mailBody: "", taPreview: {} };
        });
        return;
      }
      const draft = stateToDraftIn(state);
      void composeAnfrageMail(draft)
        .then((data) => {
          if (!wizardOpenRef.current) return;
          setWizardState((s) => ({
            ...s,
            mailSubject: data.subject || "",
            mailBody: data.body || "",
            taPreview: data.ta_nummern || {},
          }));
        })
        .catch(() => {
          /* compose errors are non-fatal in wizard */
        });
    }, 400);
  }, []);

  useEffect(() => {
    if (!wizardOpen) {
      if (composeTimerRef.current) {
        clearTimeout(composeTimerRef.current);
        composeTimerRef.current = null;
      }
      return;
    }
    debouncedCompose(wizardState);
  }, [
    wizardOpen,
    wizardState.recipients,
    wizardState.auftraege,
    wizardState.positionen,
    wizardState.snippetTemplateId,
    wizardState.kuerzel,
    debouncedCompose,
  ]);

  const openNew = useCallback(() => {
    clearLocalDraft();
    setMailReview({ open: false, items: [], groupId: null, pendingCleanup: false, auftragIds: [] });
    setWizardState(emptyWizardState(kuerzel));
    setWizardTitle("Neue Anfrage");
    setWizardOpen(true);
  }, [kuerzel]);

  const openDraft = useCallback(
    (d: AnfrageDraftOut, opts?: { sourceMaId?: number; title?: string }) => {
      const next = draftToWizardState(d, kuerzel);
      if (opts?.sourceMaId) next.sourceMaId = opts.sourceMaId;
      if (opts?.sourceMaId || next.sourceMaId) next.kuerzel = kuerzel;
      setWizardState(next);
      setWizardTitle(opts?.title ?? "Anfrage weiter bearbeiten");
      setWizardOpen(true);
    },
    [kuerzel],
  );

  const openDraftById = useCallback(
    async (id: number, opts?: { sourceMaId?: number; title?: string }) => {
      const list = await listAnfrageDrafts();
      const d = list.find((x) => x.id === id);
      if (d) openDraft(d, opts);
      else emitNotice("Entwurf nicht gefunden.");
    },
    [openDraft, emitNotice],
  );

  useImperativeHandle(ref, () => ({ openNew, openDraft, openDraftById }), [
    openNew,
    openDraft,
    openDraftById,
  ]);

  const saveDraft = useCallback(async (): Promise<boolean> => {
    const state = wizardStateRef.current;
    const payload = stateToDraftIn(state);
    try {
      let savedId = state.draftId;
      if (savedId) {
        try {
          await updateAnfrageDraft(savedId, payload);
        } catch (err) {
          if (err instanceof ApiError && err.status === 404) {
            const d = await createAnfrageDraft(payload);
            savedId = d.id;
          } else {
            throw err;
          }
        }
      } else {
        const d = await createAnfrageDraft(payload);
        savedId = d.id;
      }
      if (savedId) {
        setWizardState((s) => ({ ...s, draftId: savedId }));
        persistLocalDraft({ ...state, draftId: savedId });
      }
      emitChanged();
      return true;
    } catch {
      return false;
    }
  }, [emitChanged]);

  const cleanupAfterMail = useCallback(async () => {
    const draftId = wizardStateRef.current.draftId;
    setMailReview({ open: false, items: [], groupId: null, pendingCleanup: false, auftragIds: [] });
    if (draftId) {
      try {
        await deleteAnfrageDraft(draftId);
      } catch {
        /* ignore */
      }
    }
    clearLocalDraft();
    setWizardOpen(false);
    setWizardState(emptyWizardState(kuerzel));
    emitChanged();
  }, [kuerzel, emitChanged]);

  const openMailReview = useCallback(
    async (
      items: AnfrageMailReviewItem[],
      groupId: number | null,
      pendingCleanup: boolean,
      auftragIds: number[],
    ) => {
      if (!items.length) {
        emitNotice("Keine Mail-Daten.");
        return;
      }
      setWizardOpen(false);
      setMailReview({ open: true, items, groupId, pendingCleanup, auftragIds });
    },
    [emitNotice],
  );

  const finalizeAndReview = useCallback(async () => {
    const state = wizardStateRef.current;
    const vErr = validateTaRecipients(state.recipients);
    if (vErr) {
      emitNotice(vErr);
      return;
    }
    if (!state.positionen.length) {
      emitNotice("Bitte mindestens eine Position hinzufügen.");
      return;
    }

    const draft = stateToDraftIn(state);
    const multi = (draft.zusatz_empfaenger || []).length > 0;

    try {
      if (multi) {
        const data = await finalizeAnfrageMulti({ draft, mark_sent: false });
        const results = data.results || [];
        const items = await buildAnfrageMailReviewItems(
          results.map((row) => ({
            anfrage_id: row.anfrage_id,
            to: row.supplier_email || "",
            supplier_label: row.supplier_name || "",
            subject: row.subject || state.mailSubject,
            body: row.body || state.mailBody,
          })),
        );
        await openMailReview(items, data.anfrage_group_id ?? null, true, state.auftraege.map((a) => a.id));
        return;
      }

      const data = await finalizeAnfrage({
        draft,
        anfrage_id: state.draftId,
        mark_sent: false,
      });
      const r0 = state.recipients[0];
      const supplierLabel =
        r0?.mode === "hero" ? r0.supplier?.name || "" : r0?.manual?.firm || "";
      await openMailReview(
        await buildAnfrageMailReviewItems([
          {
            anfrage_id: data.anfrage_id,
            to: data.supplier_email || firstRecipientEmail(state),
            supplier_label: data.supplier_name || supplierLabel,
            subject: (data.subject || state.mailSubject).trim(),
            body: data.body || state.mailBody,
          },
        ]),
        null,
        true,
        state.auftraege.map((a) => a.id),
      );
    } catch (err) {
      emitNotice(err instanceof Error ? err.message : "Anfrage konnte nicht finalisiert werden.");
    }
  }, [openMailReview, emitNotice]);

  const handleMailReviewResult = useCallback(
    async (result: TaMailReviewResult) => {
      const review = mailReviewRef.current;
      if (result.action === "cancel") {
        setMailReview((m) => ({ ...m, open: false }));
        setWizardOpen(true);
        return;
      }

      const items = result.items;
      if (!items.length) return;

      const pendingCleanup = review.pendingCleanup;
      setMailReview((m) => ({ ...m, open: false }));

      try {
        if (result.action === "mailto") {
          if (result.sharepointItemIds.length || result.attachments.length) {
            emitNotice(
              "SharePoint- und lokale Anhänge bitte manuell im Mailprogramm hinzufügen oder Direktversand nutzen.",
            );
          }
          const it = items[0];
          if (!it) return;
          await patchAnfrageMailContent(it.anfrage_id, {
            subject: it.subject,
            body: it.bodyPlain,
            mark_sent: true,
          });
          const href = buildMailtoHref(it.to, it.subject, it.bodyPlain);
          const a = document.createElement("a");
          a.href = href;
          a.rel = "noopener";
          a.style.display = "none";
          document.body.appendChild(a);
          a.click();
          a.remove();
          if (items.length > 1) {
            emitNotice(
              "Erste Firma ins Mailprogramm übernommen — weitere Mails bitte einzeln im Versandfenster prüfen.",
            );
          } else {
            emitNotice("Mailprogramm geöffnet.");
          }
        } else if (result.action === "graph") {
          const attachPayload = result.attachments.length ? result.attachments : undefined;
          const spIds = result.sharepointItemIds.length ? result.sharepointItemIds : undefined;
          if (review.groupId) {
            const messages = items.map((it) => ({
              anfrage_id: it.anfrage_id,
              subject: it.subject,
              body: it.bodyPlain,
              html_body: it.bodyHtml,
            }));
            const res = await sendAnfrageGroupMail(review.groupId, {
              messages,
              sharepoint_item_ids: spIds,
              attachments: attachPayload,
            });
            const ok = (res.results || []).filter((x) => x.ok).length;
            const tot = (res.results || []).length;
            emitNotice(`Mail an ${ok}/${tot} Firmen gesendet.`);
          } else if (items.length > 1) {
            let ok = 0;
            for (const it of items) {
              await sendAnfrageMail(it.anfrage_id, {
                subject: it.subject,
                body: it.bodyPlain,
                html_body: it.bodyHtml,
                sharepoint_item_ids: spIds,
                attachments: attachPayload,
              });
              ok += 1;
            }
            emitNotice(`${ok} Mails gesendet.`);
          } else {
            const it = items[0];
            if (!it) return;
            await sendAnfrageMail(it.anfrage_id, {
              subject: it.subject,
              body: it.bodyPlain,
              html_body: it.bodyHtml,
              sharepoint_item_ids: spIds,
              attachments: attachPayload,
            });
            emitNotice("Mail gesendet.");
          }
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 412) {
          emitNotice("Microsoft-Anmeldung muss erneuert werden.");
        } else {
          emitNotice(err instanceof Error ? err.message : "Versand fehlgeschlagen.");
        }
        setMailReview((m) => ({
          ...m,
          open: true,
          items,
          groupId: review.groupId,
          pendingCleanup,
          auftragIds: review.auftragIds,
        }));
        return;
      }

      if (pendingCleanup) {
        await cleanupAfterMail();
      } else {
        setMailReview({ open: false, items: [], groupId: null, pendingCleanup: false, auftragIds: [] });
        emitChanged();
      }
    },
    [cleanupAfterMail, emitNotice, emitChanged],
  );

  const closeWizard = useCallback(
    async (saveOnClose: boolean) => {
      const state = wizardStateRef.current;
      if (saveOnClose && state.positionen.length) {
        const ok = await saveDraft();
        if (ok) clearLocalDraft();
      } else if (!state.positionen.length && state.draftId) {
        try {
          await deleteAnfrageDraft(state.draftId);
        } catch {
          /* ignore */
        }
        clearLocalDraft();
      } else if (!state.positionen.length) {
        clearLocalDraft();
      }
      setWizardOpen(false);
      emitChanged();
    },
    [saveDraft, emitChanged],
  );

  return (
    <>
      <NewAnfrageDialog
        open={wizardOpen}
        state={wizardState}
        setState={setWizardState}
        title={wizardTitle}
        onClose={() => void closeWizard(true)}
        onSaveDraft={async () => {
          if (!wizardStateRef.current.positionen.length) {
            await closeWizard(false);
            return;
          }
          const ok = await saveDraft();
          if (ok) {
            emitNotice("Entwurf gespeichert.");
            clearLocalDraft();
            setWizardOpen(false);
          } else {
            emitNotice("Entwurf konnte nicht gespeichert werden.");
          }
        }}
        onSend={() => void finalizeAndReview()}
      />

      <TaMailReviewDialog
        open={mailReview.open}
        items={mailReview.items}
        auftragIds={mailReview.auftragIds}
        isEntra={isEntra}
        onResolve={(r) => void handleMailReviewResult(r)}
      />
    </>
  );
});
